import { useEffect, useId, useRef, useState } from "react";
import { fetchMailOnlyOfficeConfig, type MailOnlyOfficeEditorType } from "@/lib/api/onlyoffice";
import { MessageBodyPreloader } from "./MessageBodyPreloader";
import type { MailAttachment } from "@/lib/types/mail";

type DocsEditor = {
  destroyEditor?: () => void;
};

type DocsApiWindow = Window & {
  DocsAPI?: {
    DocEditor: new (id: string, config: Record<string, unknown>) => DocsEditor;
  };
};

function loadOnlyOfficeApi(documentServerUrl: string): Promise<void> {
  const base = documentServerUrl.replace(/\/+$/u, "");
  if (!base) return Promise.reject(new Error("missing_document_server"));
  const w = window as DocsApiWindow;
  if (w.DocsAPI?.DocEditor) return Promise.resolve();

  const existing = document.querySelector('script[data-mail-onlyoffice="1"]');
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("script_load")), { once: true });
      if ((window as DocsApiWindow).DocsAPI?.DocEditor) resolve();
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `${base}/web-apps/apps/api/documents/api.js`;
    script.async = true;
    script.dataset.mailOnlyoffice = "1";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("script_load"));
    document.head.appendChild(script);
  });
}

type Props = {
  accountId: number;
  folder: string;
  messageUid: number;
  attachment: MailAttachment;
  editorType: MailOnlyOfficeEditorType;
};

export function MailOfficePreview({
  accountId,
  folder,
  messageUid,
  attachment,
  editorType,
}: Props) {
  const reactId = useId().replace(/:/g, "");
  const hostId = `mail-oo-${reactId}`;
  const editorRef = useRef<DocsEditor | null>(null);
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPhase("loading");
    setError(null);

    void (async () => {
      const result = await fetchMailOnlyOfficeConfig({
        accountId,
        folder,
        uid: messageUid,
        part: attachment.part,
        filename: attachment.filename,
        mime: attachment.mime,
        size: attachment.size,
        editorType,
      });
      if (cancelled) return;
      if (!result.ok) {
        setError(
          result.error === "onlyoffice_not_configured"
            ? "Aperçu Office indisponible. Tu peux télécharger le fichier."
            : result.message || "Impossible d’ouvrir l’aperçu Office."
        );
        setPhase("error");
        return;
      }

      const dsUrl = result.data.document_server_url;
      const config = result.data.config;
      if (!dsUrl || !config) {
        setError("Réponse serveur incomplète.");
        setPhase("error");
        return;
      }

      try {
        await loadOnlyOfficeApi(dsUrl);
      } catch {
        if (!cancelled) {
          setError("OnlyOffice est injoignable. Tu peux télécharger le fichier.");
          setPhase("error");
        }
        return;
      }
      if (cancelled) return;

      const el = document.getElementById(hostId);
      const DocEditor = (window as DocsApiWindow).DocsAPI?.DocEditor;
      if (!el || !DocEditor) {
        setError("Éditeur OnlyOffice indisponible.");
        setPhase("error");
        return;
      }

      if (editorRef.current?.destroyEditor) {
        try {
          editorRef.current.destroyEditor();
        } catch {
          /* ignore */
        }
      }

      editorRef.current = new DocEditor(hostId, {
        ...config,
        width: "100%",
        height: "100%",
        events: {
          onDocumentReady: () => {
            if (!cancelled) setPhase("ready");
          },
          onError: (event: { data?: { errorCode?: string | number } | string | number }) => {
            const code =
              typeof event?.data === "object" && event.data
                ? event.data.errorCode
                : event?.data;
            setError(`Impossible d’afficher ce document (${code ?? "?"}). Tu peux le télécharger.`);
            setPhase("error");
          },
        },
      });
    })();

    return () => {
      cancelled = true;
      if (editorRef.current?.destroyEditor) {
        try {
          editorRef.current.destroyEditor();
        } catch {
          /* ignore */
        }
      }
      editorRef.current = null;
    };
  }, [
    accountId,
    attachment.filename,
    attachment.mime,
    attachment.part,
    attachment.size,
    editorType,
    folder,
    hostId,
    messageUid,
  ]);

  return (
    <div className="absolute inset-0 min-h-0 bg-[var(--mail-bg)]">
      {phase === "loading" ? (
        <MessageBodyPreloader overlay label="Ouverture du document…" />
      ) : null}
      {phase === "error" && error ? (
        <div className="flex h-full min-h-[8rem] items-center justify-center p-4">
          <p className="mail-alert-error text-sm" role="alert">
            {error}
          </p>
        </div>
      ) : null}
      <div
        id={hostId}
        className={`absolute inset-0 h-full w-full ${phase === "error" ? "hidden" : ""}`}
      />
    </div>
  );
}
